import React, { forwardRef } from 'react';
import { cn } from '@/utils/cn';

export interface ITextAreaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
    /**
     * The visual style variant of the textarea
     */
    variant?: 'default' | 'error' | 'success';

    /**
     * The size of the textarea
     */
    size?: 'sm' | 'md' | 'lg';

    /**
     * Whether the textarea has full width
     */
    fullWidth?: boolean;

    /**
     * Error message to display below the textarea
     */
    error?: string;

    /**
     * Help text to display below the textarea
     */
    helpText?: string;

    /**
     * Whether to disable resizing
     */
    resize?: 'none' | 'both' | 'horizontal' | 'vertical';
}

/**
 * Shared TextArea component following BiBi design system standards
 * 
 * Provides consistent styling with Input component and textarea-specific features
 * 
 * @example
 * ```tsx
 * <TextArea
 *   placeholder="Enter your message"
 *   rows={4}
 *   error={errors.message?.message}
 *   resize="vertical"
 *   {...register('message')}
 * />
 * ```
 */
export const TextArea = forwardRef<HTMLTextAreaElement, ITextAreaProps>(
    (
        {
            variant = 'default',
            size = 'md',
            fullWidth = true,
            error,
            helpText,
            resize = 'vertical',
            className,
            disabled,
            ...props
        },
        ref
    ) => {
        // Determine focus ring color based on variant
        const focusRingColor = error
            ? 'focus:ring-red-500 focus:border-red-500'
            : 'focus:ring-bibi-blue focus:border-bibi-blue';

        // Base textarea classes following BiBi design system
        const baseTextAreaClasses = cn(
            // Layout
            'border rounded-lg transition-colors outline-none',

            // Typography
            'font-medium placeholder:text-gray-500',

            // States
            'hover:border-gray-400',
            'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
            'disabled:border-gray-200',

            // Focus states
            'focus:ring-2 focus:border-transparent',
            focusRingColor,

            // Variant styles
            {
                // Default variant
                'border-gray-300 bg-white text-gray-900': variant === 'default' && !error,

                // Error variant
                'border-red-300 bg-red-50 text-red-900 placeholder:text-red-400': variant === 'error' || !!error,

                // Success variant  
                'border-green-300 bg-green-50 text-green-900': variant === 'success',
            },

            // Size variants
            {
                'px-3 py-2 text-sm': size === 'sm',
                'px-4 py-3 text-base': size === 'md',
                'px-5 py-4 text-lg': size === 'lg',
            },

            // Width
            {
                'w-full': fullWidth,
            },

            // Resize behavior
            {
                'resize-none': resize === 'none',
                'resize': resize === 'both',
                'resize-x': resize === 'horizontal',
                'resize-y': resize === 'vertical',
            },

            className
        );

        return (
            <div className={cn('relative', { 'w-full': fullWidth })}>
                {/* TextArea Field */}
                <textarea
                    ref={ref}
                    className={baseTextAreaClasses}
                    disabled={disabled}
                    aria-invalid={error ? 'true' : 'false'}
                    aria-describedby={
                        error ? `${props.id}-error` :
                            helpText ? `${props.id}-help` : undefined
                    }
                    {...props}
                />

                {/* Error Message */}
                {error && (
                    <p
                        id={`${props.id}-error`}
                        className="mt-2 text-sm text-red-600"
                        role="alert"
                    >
                        {error}
                    </p>
                )}

                {/* Help Text */}
                {helpText && !error && (
                    <p
                        id={`${props.id}-help`}
                        className="mt-2 text-sm text-gray-500"
                    >
                        {helpText}
                    </p>
                )}
            </div>
        );
    }
);

TextArea.displayName = 'TextArea';
